/**
 * The options of a choice question — and the scale of a grid, which is the same list.
 *
 * `value` is a **code**, one for the three languages, written into every answer row. It is
 * generated from the first label typed and then left alone, and it is locked once the survey has
 * answers: renaming it would split that option's count in two, one half under the old code and
 * one under the new, with no error anywhere (the same reason a question `key` freezes, R5).
 *
 * Removing an option does **not** remove the answers behind it. They keep the stored `value`, so
 * the results screen goes on counting them under a code with no label — which is why the remove
 * button says so before it acts.
 *
 * @module Pages/BUs/Surveys
 */

import { css } from '@emotion/css';
import { UTButton, UTTextField } from '@group-link-one/gl-ui/utilities';
import type { JSX } from 'react';
import type { SurveyLanguage } from '../../../Types/Surveys.types';
import { SURVEY_LIMITS } from '../../../Types/Surveys.types';
import type { SurveyOptionDraft } from '../../../Utils/surveyForm';
import { BASE_LANGUAGE } from '../../../Utils/surveyForm';
import { IconButton } from '@/Shared/components/IconButton/IconButton';

const wrapStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-s);
`;

const headStyle = css`
  display: flex;
  align-items: baseline;
  gap: var(--space-s);
  flex-wrap: wrap;
`;

const titleStyle = css`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
`;

const hintStyle = css`
  margin: 0;
  flex: 1 1 240px;
  min-width: 0;
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
  text-wrap: pretty;
`;

const rowStyle = css`
  display: grid;
  grid-template-columns: 26px minmax(0, 200px) minmax(0, 1fr) auto auto;
  align-items: center;
  gap: var(--space-s);
`;

const indexStyle = css`
  font-family: var(--font-mono);
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
  text-align: right;
`;

const flagsStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-s);
`;

const flagStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: var(--font-size-2xs);
  color: var(--color-text-secondary);
  white-space: nowrap;
  cursor: pointer;
`;

const actionsStyle = css`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const footerStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-s);
`;

const countStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

export interface QuestionOptionsEditorProps {
  id: string;
  /** Grid scales and choice lists are the same list; only the wording around it differs. */
  isScale: boolean;
  options: SurveyOptionDraft[];
  language: SurveyLanguage;
  /** Locked once the survey has answers — the codes are already written on collected data. */
  frozen: boolean;
  onValueChange: (optionUid: string, value: string) => void;
  onLabelChange: (optionUid: string, value: string) => void;
  onFlagChange: (optionUid: string, flag: 'isExclusive' | 'allowsFreeText', value: boolean) => void;
  onMove: (optionUid: string, direction: -1 | 1) => void;
  onAdd: () => void;
  onRemove: (optionUid: string) => void;
}

export const QuestionOptionsEditor = ({
  id,
  isScale,
  options,
  language,
  frozen,
  onValueChange,
  onLabelChange,
  onFlagChange,
  onMove,
  onAdd,
  onRemove,
}: QuestionOptionsEditorProps): JSX.Element => (
  <div className={wrapStyle}>
    <div className={headStyle}>
      <span className={titleStyle}>{isScale ? 'Escala da grade' : 'Opções'}</span>
      <p className={hintStyle}>
        {isScale
          ? 'A ordem é a escala: a primeira opção vale 1, e é dela que sai a média de cada linha.'
          : 'O código fica igual nos três idiomas e é o que aparece no CSV. Só o rótulo é traduzido.'}
      </p>
    </div>

    {options.map((option, index) => (
      <div key={option.uid} className={rowStyle}>
        <span className={indexStyle}>{index + 1}</span>

        <UTTextField
          id={`${id}-option-value-${index}`}
          placeholder='codigo_da_opcao'
          value={option.value}
          disabled={frozen || language !== BASE_LANGUAGE}
          onChange={event => {
            onValueChange(option.uid, event.target.value);
          }}
        />

        <UTTextField
          id={`${id}-option-label-${index}`}
          placeholder='Rótulo que a pessoa lê'
          value={option.labels[language]}
          onChange={event => {
            onLabelChange(option.uid, event.target.value);
          }}
        />

        <div className={flagsStyle}>
          {isScale ? null : (
            <>
              <label className={flagStyle} htmlFor={`${id}-option-exclusive-${index}`}>
                <input
                  id={`${id}-option-exclusive-${index}`}
                  type='checkbox'
                  checked={option.isExclusive}
                  onChange={event => {
                    onFlagChange(option.uid, 'isExclusive', event.target.checked);
                  }}
                />
                exclusiva
              </label>
              <label className={flagStyle} htmlFor={`${id}-option-free-text-${index}`}>
                <input
                  id={`${id}-option-free-text-${index}`}
                  type='checkbox'
                  checked={option.allowsFreeText}
                  onChange={event => {
                    onFlagChange(option.uid, 'allowsFreeText', event.target.checked);
                  }}
                />
                "outro"
              </label>
            </>
          )}
        </div>

        <div className={actionsStyle}>
          <IconButton
            id={`${id}-option-up-${index}`}
            icon='Arrows.ArrowUp'
            size='sm'
            aria-label={`Subir a opção ${index + 1}`}
            disabled={index === 0}
            onClick={() => {
              onMove(option.uid, -1);
            }}
          />
          <IconButton
            id={`${id}-option-down-${index}`}
            icon='Arrows.ArrowDown'
            size='sm'
            aria-label={`Descer a opção ${index + 1}`}
            disabled={index === options.length - 1}
            onClick={() => {
              onMove(option.uid, 1);
            }}
          />
          <IconButton
            id={`${id}-option-remove-${index}`}
            icon='Misc.Trash'
            size='sm'
            variant='danger'
            aria-label={`Remover a opção ${index + 1}`}
            onClick={() => {
              onRemove(option.uid);
            }}
          />
        </div>
      </div>
    ))}

    <div className={footerStyle}>
      <UTButton
        fitContent
        variant='secondary'
        size='small'
        id={`${id}-option-add`}
        text={isScale ? 'Novo nível' : 'Nova opção'}
        icon='Arrows.Plus'
        disabled={options.length >= SURVEY_LIMITS.optionsPerQuestion}
        onClick={onAdd}
      />
      <span className={countStyle}>
        {options.length} de {SURVEY_LIMITS.optionsPerQuestion}
        {frozen ? ' · códigos travados: a pesquisa já tem resposta' : ''}
      </span>
    </div>
  </div>
);
